import { useCallback, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import { fg } from '@atlassian/jira-feature-gating';
import {
	fireOperationalAnalytics,
	fireTrackAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import { expVal, expValEquals } from '@atlassian/jira-feature-experiments';
import { useService } from '@atlassian/jira-expansion-services/src/index.tsx';
import { requestAccess as requestAccessExpansionService } from '@atlassian/expansion-services/services';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { requestAccess } from '../../services/access-request/index.tsx';
import { fireErrorAnalytics } from '../../services/analytics/index.tsx';
import {
	snoozeFeatureFor30Days,
	increaseFeatureVisitedCount,
	removeFeatureVisited,
	cjnEligibilityCacheHandler,
	withEligibilityCache,
	setHasSeenNudgeInLast7Days,
} from '../../services/caching/index.tsx';
import { useFilteredUsersFromCollaborationGraph } from '../../services/get-collaborators/index.tsx';
import { useNudgeStateV2a } from '../../services/nudge-state/index.tsx';
import { useNudgeRecommendations } from '../../services/recommendations/index.tsx';
import { generateCJNContent } from '../../services/generated-content/index.tsx';
import { useRequestAccessErrorFlag } from '../request-access-error-flag/index.tsx';
import {
	getContextualBanditAnalyticsAttributes,
	getContextualBanditCJNMessageParser,
	getLLMAnalyticsAttributes,
	getLLMCJNMessageParser,
	useRequestAccessPromptFlag,
} from '../request-access-prompt-flag/index.tsx';
import type { AccessRequestType } from '../../common/types.tsx';
import { useRequestAccessSuccessFlag } from '../request-access-success-flag/index.tsx';

const MAX_NUMBER_OF_CONFLUENCE_COLLABORATORS = 5;

type UseConfluenceCrossJoinNudgeArguments = {
	featureVisitedCount: number;
};

export const useConfluenceCrossJoinNudge = () => {
	const sessionId = useRef(uuid());
	const getNudgeRecommendations = useNudgeRecommendations({ sessionId: sessionId.current });
	const cloudId = useCloudId();
	const getFilteredCollaborators = useFilteredUsersFromCollaborationGraph(
		cloudId,
		'confluence',
		MAX_NUMBER_OF_CONFLUENCE_COLLABORATORS,
	);
	const showRequestAccessPromptFlag = useRequestAccessPromptFlag();
	const showSuccessFlag = useRequestAccessSuccessFlag();
	const showErrorFlag = useRequestAccessErrorFlag();
	const { setNudgeDismissed: setNudgeDismissedV2a } = useNudgeStateV2a();
	const { push } = useRouterActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const userId = useAccountId() || '';
	const locale = useLocale();

	const requestAccessService = fg('expansion_services_in_cjn')
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useService(requestAccessExpansionService)
		: null;

	return useCallback(
		async ({ featureVisitedCount }: UseConfluenceCrossJoinNudgeArguments, allowLlm = true) => {
			const startTimestamp = Date.now();
			try {
				async function onSubmit() {
					try {
						try {
							let accessStatus: AccessRequestType;
							if (requestAccessService && fg('expansion_services_in_cjn')) {
								const serviceResult = await requestAccessService({
									product: 'confluence',
									integration: 'crossJoinNudgesConfluence',
								});

								if (serviceResult.error) {
									fireErrorAnalytics('crossJoinNudgesConfluence', serviceResult.error);
									throw serviceResult.error;
								}
								accessStatus = serviceResult.result;
							} else {
								accessStatus = await requestAccess(
									'confluence',
									cloudId,
									userId,
									'crossJoinNudgesConfluence',
									createAnalyticsEvent,
								);
							}
							if (
								accessStatus === 'ACCESS_GRANTED' ||
								accessStatus === 'ACCESS_EXISTS' ||
								accessStatus === 'APPROVED_REQUEST_EXISTS'
							) {
								push('/wiki');
							} else if (
								accessStatus === 'PENDING_REQUEST_CREATED' ||
								accessStatus === 'PENDING_REQUEST_EXISTS'
							) {
								showSuccessFlag();
							} else {
								showErrorFlag('confluence');
							}
						} catch {
							showErrorFlag('confluence');
						}
					} catch {
						showErrorFlag('confluence');
					}
				}

				const isEnglishLocale = locale && locale.startsWith('en');

				const collaborators = await getFilteredCollaborators();
				const hasNoCollaborators = collaborators.length === 0;

				const cachedLlmNewUser = withEligibilityCache('user_new_for_llm_exp', () =>
					Promise.resolve(featureVisitedCount === 0),
				);
				const userNeverSawConfNudgeBeforeLlmExp = await cachedLlmNewUser();

				if (fg('grw-1755-growth-exp-jira-cjn-llm-gate')) {
					if (
						allowLlm &&
						isEnglishLocale &&
						hasNoCollaborators &&
						userNeverSawConfNudgeBeforeLlmExp === true
					) {
						/**
						 * In order to avoid an experiment bias for grw-1755-growth-exp-jira-cjn-llm we need to
						 * pregenerate content and return early for all cohorts if content isn't available
						 * https://atlassian.slack.com/archives/C08RDQB03EJ/p1755151418360249?thread_ts=1755147153.025819&cid=C08RDQB03EJ
						 */
						const result = await generateCJNContent();
						if (!result.ready) {
							removeFeatureVisited('crossJoinNudgesConfluence');
							return;
						}

						fireTrackAnalytics(createAnalyticsEvent({}), 'feature exposed', {
							flagKey: 'grw-1755-growth-exp-jira-cjn-llm',
							enrolment: expVal('grw-1755-growth-exp-jira-cjn-llm', 'cohort', 'not_enrolled'),
						});
						if (expValEquals('grw-1755-growth-exp-jira-cjn-llm', 'cohort', 'Variation')) {
							showRequestAccessPromptFlag({
								users: collaborators,
								onSubmit,
								onOptout: setNudgeDismissedV2a,
								snoozeFor30Days: () => snoozeFeatureFor30Days('crossJoinNudgesConfluence'),
								integration: 'crossJoinNudgesConfluence',
								featureVisitedCount,
								messageParser: getLLMCJNMessageParser({
									title: result.title,
									description: result.body,
								}),
								eventAttributes: getLLMAnalyticsAttributes({
									users: collaborators,
									userId,
									sessionId: sessionId.current,
									featureVisitedCount,
									startTimestamp,
									companyName: result.metadata.companyName,
									firstName: result.metadata.firstName,
									generatedBody: result.body,
									generatedTitle: result.title,
								}),
							});

							await Promise.all([
								increaseFeatureVisitedCount(userId, featureVisitedCount + 1),
								cjnEligibilityCacheHandler.set('has_seen_conf', { value: true }),
								cjnEligibilityCacheHandler.set('has_seen_conf_llm', { value: true }),
							]);

							fireOperationalAnalytics(
								createAnalyticsEvent({}),
								'slo.crossJoinNudgesConfluence succeeded',
							);

							return;
						}
					}
				}

				const entityId = hasNoCollaborators ? await getNudgeRecommendations() : undefined;
				showRequestAccessPromptFlag({
					users: collaborators,
					onSubmit,
					onOptout: setNudgeDismissedV2a,
					snoozeFor30Days: () => snoozeFeatureFor30Days('crossJoinNudgesConfluence'),
					integration: 'crossJoinNudgesConfluence',
					featureVisitedCount,
					messageParser: getContextualBanditCJNMessageParser({
						isContextualBandit: hasNoCollaborators,
						entityId: entityId || 'default',
						featureVisitedCount,
					}),
					eventAttributes: getContextualBanditAnalyticsAttributes({
						users: collaborators,
						userId,
						entityId: entityId || 'default',
						sessionId: sessionId.current,
						isContextualBandit: hasNoCollaborators,
						featureVisitedCount,
						startTimestamp,
					}),
				});
				await Promise.all([
					increaseFeatureVisitedCount(userId, featureVisitedCount + 1),
					cjnEligibilityCacheHandler.set('has_seen_conf', { value: true }),
					setHasSeenNudgeInLast7Days(),
				]);

				fireOperationalAnalytics(
					createAnalyticsEvent({}),
					'slo.crossJoinNudgesConfluence succeeded',
				);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics('useCrossJoinNudges', error, { cloudId });
			}
		},
		[
			cloudId,
			createAnalyticsEvent,
			getFilteredCollaborators,
			getNudgeRecommendations,
			locale,
			push,
			requestAccessService,
			setNudgeDismissedV2a,
			showErrorFlag,
			showRequestAccessPromptFlag,
			showSuccessFlag,
			userId,
		],
	);
};
